import React from 'react';

import { FeatureToggles } from '@paralleldrive/react-feature-toggles';

import Alerts from '@components/Alerts';
import Footer from '@components/Footer';
import Header from '@components/Header';
import { titleTemplateParams } from '@components/Title/util';
import ImpersonationHeader from '@components/User/ImpersonationHeader';

import Auth0Layout from './auth0Layout';
import { getClientErrorNotification } from '../utils/getClientErrorNotification';

import { NOTIFICATIONS_WRAPPER_ID } from '@domain/notifications';

import { useNotifications } from '@use-cases/notifications';

import ApolloCustomProvider from '@utils/clients';
import {
  enabledFeatures,
  FEATURE_AUTH0_LOGIN,
  FEATURE_COOKIE_PRO,
  FEATURE_SITECORE_FOOTER,
  FEATURE_SITECORE_HEADER,
  isEnabled,
} from '@utils/features';
import { delocalizedPath } from '@utils/localized-path';

import AppConfigProvider from '@domui-hooks/appConfig';
import SitecoreFooter from '@domui-sitecore/wrappers/SitecoreFooter';
import SitecoreHeader from '@domui-sitecore/wrappers/SitecoreHeader';
import { ErrorCode } from '@domui-utils/ErrorCode';
import Workflow, {
  DominoAxiosResponse,
  DominoError,
} from '@domui-utils/workflow/base';

import { Helmet, HelmetProvider } from '@external/react-helmet-async';
import { useTranslation } from '@external/react-i18next';
import { useAnalytics } from '@hooks/analytics';

declare global {
  interface OptanonApi {
    changeLanguage?: (lang: string) => void;
    ToggleInfoDisplay?: () => void;
  }

  interface Window {
    Optanon?: OptanonApi;
  }
}

const SitecoreLayout: React.FC<{
  location: Location;

  pageContext: {
    language: string;
    layout?: string;
    iframe?: boolean;
  };
}> = ({ children, location, pageContext }) => {
  const { t } = useTranslation();
  const { suffix } = titleTemplateParams(t);
  const { updateAnalyticsMetadata, updateAnalyticsPageData } = useAnalytics();
  const { addError, clearNotification } = useNotifications();
  const isAuth0LoginEnabled = isEnabled(FEATURE_AUTH0_LOGIN);

  const [error, setError] = React.useState<DominoError | null>(null);
  const setErrorNotification = (code: ErrorCode, message: string) => {
    const notificationId = `error-${code}`;
    clearNotification(notificationId);
    addError(message, { id: notificationId });
  };
  const isAxiosResponse = (err: DominoError): err is DominoAxiosResponse =>
    (err as DominoAxiosResponse)?.data?.wfRes?.responseData !== undefined;

  React.useEffect(() => {
    Workflow.setReactErrorHandler((err: DominoError) => {
      setError((err as unknown) as DominoError);
    });
  }, []);

  React.useEffect(() => {
    if (
      isEnabled(FEATURE_COOKIE_PRO) &&
      window?.Optanon &&
      window.Optanon?.changeLanguage
    ) {
      window.Optanon?.changeLanguage(pageContext.language);
    }
  }, [pageContext.language]);

  if (error && isAxiosResponse(error)) {
    if (error?.data?.wfRes?.responseData?.status === 401) {
      getClientErrorNotification(error, setErrorNotification, t);
    }
  }

  updateAnalyticsPageData(pageContext);

  if (pageContext.layout === 'plain') {
    return <React.Fragment key="value">{children}</React.Fragment>;
  }

  return (
    <ApolloCustomProvider>
      <FeatureToggles features={enabledFeatures}>
        <AppConfigProvider
          language={pageContext.language}
          path={delocalizedPath(location.pathname)}
        >
          <HelmetProvider>
            <Helmet
              onChangeClientState={updateAnalyticsMetadata}
              htmlAttributes={{ lang: pageContext.language }}
              titleTemplate={`%s ${suffix}`}
              defaultTitle={suffix}
            />
            <ImpersonationHeader />
            {isAuth0LoginEnabled ? (
              <div className="mainContainer min-h-full">
                {isEnabled(FEATURE_SITECORE_HEADER) ? (
                  <SitecoreHeader />
                ) : (
                  <Header />
                )}
                <Alerts />
                <div id={NOTIFICATIONS_WRAPPER_ID} />
                <main className="flex-1 tablet:pt-1">
                  <Auth0Layout>{children}</Auth0Layout>
                </main>
                {isEnabled(FEATURE_SITECORE_FOOTER) ? (
                  <SitecoreFooter />
                ) : (
                  <Footer />
                )}
              </div>
            ) : (
              <Auth0Layout>{children}</Auth0Layout>
            )}
          </HelmetProvider>
        </AppConfigProvider>
      </FeatureToggles>
    </ApolloCustomProvider>
  );
};

export default SitecoreLayout;
